import { makeStyles } from "tss-react/mui";
import logo from "../images/logot/Kirketeksti_valkoinen_cropped.png";
import IconImage from "../assets/iconImage";
import insta from "../assets/icons/instagram/Instagram_Glyph_Gradient.png";
import instaWhite from "../assets/icons/instagram/Instagram_Glyph_White.png";
import fb from "../assets/icons/facebook/Facebook_Logo_Primary.png";
import fbWhite from "../assets/icons/facebook/Facebook_Logo_Secondary.png";
import spotify from "../assets/icons/spotify/Spotify_Primary_Logo_RGB_Green.png";
import spotifyWhite from "../assets/icons/spotify/Spotify_Primary_Logo_RGB_White.png";
import { primaryColor, secondaryColor } from "../assets/colors";
import { useIsMobile } from "../utils/useIsMobile";
import { useResponsiveSize } from "../utils/useResponsiveSize";
import MenuIcon from "@mui/icons-material/Menu";

type NavProps = {
  onNavigate?: (section: string) => void;
  handleOpenMenu: () => void;
};

const useStyles = makeStyles<{ navHeight: number }>()((theme, params) => ({
  navigationContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: params.navHeight,
    display: "flex",
    alignItems: "center",
    backgroundColor: "black",
  },
  navigationContainerDesktop: {
    padding: "0 112px",
  },
  navigationContainerMobile: {
    padding: "0 32px",
  },
  navigationWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  logoImage: {
    width: 220,
  },
  rightGroup: {
    display: "flex",
    marginLeft: "auto",
  },
  navSection: {
    display: "flex",
    alignItems: "center",
    color: primaryColor,
    fontFamily: "League Gothic",
    fontSize: 30,
    fontWeight: 600,
    letterSpacing: "1.2px",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    margin: "0px 16px",
    "&:hover": {
      color: secondaryColor,
      cursor: "pointer",
    },
  },
  navItemMobile: {
    display: "flex",
    alignItems: "center",
    margin: "0px 16px",
  },
  kirkeLogo: {
    backgroundColor: "white",
    // backgroundColor: primaryColor,
    WebkitMask: `url(${logo}) no-repeat center / contain`,
    mask: `url(${logo}) no-repeat center / contain`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    "&:hover": {
      backgroundColor: secondaryColor, // keep in sync with navItem hover color
      cursor: "pointer",
    },
  },
  kirkeLogoDesktop: {
    width: "220px",
    height: "110px",
  },
  kirkeLogoMobile: {
    width: "110px",
    height: "55px",
  },
  whiteInstaIcon: {
    WebkitMask: `url(${instaWhite}) no-repeat center / contain`,
    mask: `url(${instaWhite}) no-repeat center / contain`,
  },
  whiteFacebookIcon: {
    WebkitMask: `url(${fbWhite}) no-repeat center / contain`,
    mask: `url(${fbWhite}) no-repeat center / contain`,
  },
  whiteSpotifyIcon: {
    WebkitMask: `url(${spotifyWhite}) no-repeat center / contain`,
    mask: `url(${spotifyWhite}) no-repeat center / contain`,
  },
  linkIcon: {
    backgroundColor: primaryColor, // keep in sync with navSection color
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    "&:hover": {
      backgroundColor: secondaryColor, // keep in sync with navItem hover color
    },
  },
  linkIconSize: {
    width: 30,
    height: 30,
  },
  linkIconSizeMobile: {
    width: 18,
    height: 18,
  },
  menuIcon: {
    marginLeft: "16px",
  },
}));

const NavigationComponent: React.FC<NavProps> = ({
  onNavigate,
  handleOpenMenu,
}) => {
  const navHeight = useResponsiveSize();
  const { classes } = useStyles({ navHeight });
  const isMobile = useIsMobile();
  const linkIconSize = isMobile
    ? classes.linkIconSizeMobile
    : classes.linkIconSize;
  const navItem = isMobile ? classes.navItemMobile : classes.navItem;

  const instalink = "https://www.instagram.com/kirkeband/";
  const fbLink = "https://www.facebook.com/kirkeband";
  const spotifyLink = "https://open.spotify.com/artist/0QmkuKDhfE5x8np0cChVR8";

  const iconColors = false; // change value to switch between coloured and white icons

  return (
    <div
      className={`${classes.navigationContainer} ${isMobile ? classes.navigationContainerMobile : classes.navigationContainerDesktop}`}
    >
      <div className={classes.navigationWrapper}>
        <div
          aria-hidden="true"
          className={`${classes.kirkeLogo} ${isMobile ? classes.kirkeLogoMobile : classes.kirkeLogoDesktop}`}
          onClick={() => onNavigate && onNavigate("top")}
        />
        <div className={classes.rightGroup}>
          <nav className={classes.navSection}>
            {!isMobile && (
              <>
                <div
                  className={classes.navItem}
                  onClick={() => onNavigate && onNavigate("news")}
                >
                  UUTISET
                </div>
                <div
                  className={classes.navItem}
                  onClick={() => onNavigate && onNavigate("shows")}
                >
                  KEIKAT
                </div>
                <div
                  className={classes.navItem}
                  onClick={() => onNavigate && onNavigate("band")}
                >
                  BÄNDI
                </div>
                <div
                  className={classes.navItem}
                  onClick={() => onNavigate && onNavigate("contact")}
                >
                  YHTEYS
                </div>
              </>
            )}
            {iconColors ? (
              <>
                {/* INSTAGRAM */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={instalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Instagram"
                  >
                    <IconImage
                      src={insta}
                      alt="Instagram"
                      width={30}
                      height={30}
                    />
                  </a>
                </div>
                {/* FACEBOOK */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={fbLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Facebook"
                  >
                    <IconImage src={fb} alt="Facebook" width={30} height={30} />
                  </a>
                </div>
                {/* SPOTIFY */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={spotifyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Spotify"
                  >
                    <IconImage
                      src={spotify}
                      alt="Spotify"
                      width={30}
                      height={30}
                    />
                  </a>
                </div>
              </>
            ) : (
              <>
                {/* INSTAGRAM */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={instalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Instagram"
                  >
                    <div
                      aria-hidden="true"
                      className={`${classes.whiteInstaIcon} ${classes.linkIcon} ${linkIconSize}`}
                    />
                  </a>
                </div>
                {/* FACEBOOK */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={fbLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Facebook"
                  >
                    <div
                      aria-hidden="true"
                      className={`${classes.whiteFacebookIcon} ${classes.linkIcon} ${linkIconSize}`}
                    />
                  </a>
                </div>
                {/* SPOTIFY */}
                <div className={navItem}>
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={spotifyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Kirke on Spotify"
                  >
                    <div
                      aria-hidden="true"
                      className={`${classes.whiteSpotifyIcon} ${classes.linkIcon} ${linkIconSize}`}
                    />
                  </a>
                </div>
              </>
            )}
            {isMobile && (
              <MenuIcon className={classes.menuIcon} onClick={handleOpenMenu} />
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default NavigationComponent;
